/**
 * 「加入主畫面」的提示，給監督者看。
 *
 * 三種環境的做法不同，所以先分清楚人在哪裡：
 * - **LINE**：朋友多半是點 LINE 裡的連結進來的，而 LINE 內建瀏覽器不能加入主畫面，
 *   要先換到一般瀏覽器。換過去之後 cookie 不共用，所以要再輸入一次密碼。
 * - **iOS**：分享按鈕裡有「加入主畫面」。
 * - **Android**：在瀏覽器選單裡。
 *
 * LINE 要最先判斷：它的 User-Agent 同時也帶著 iPhone 或 Android。
 * 判斷不出來或已經從主畫面開啟就回 null，寧可不提示，也不要教錯。
 *
 * 與 `src/domain/` 的鐵則一致：不讀 navigator，環境由呼叫端傳入。
 */

export type InstallPlatform = "line" | "ios" | "android";

export interface InstallEnv {
  userAgent: string;
  /** iPadOS 的 User-Agent 長得像 Mac，只能靠觸控點數分辨。 */
  maxTouchPoints: number;
  /** 已經從主畫面開啟了。 */
  standalone: boolean;
}

export interface InstallHint {
  platform: InstallPlatform;
  /** 粗體前後的文字。中間那段是使用者要找的選項名稱。 */
  before: string;
  action: string;
  after: string;
}

export function installPlatform({ userAgent, maxTouchPoints }: InstallEnv): InstallPlatform | null {
  if (/\bLine\/\d/.test(userAgent)) return "line";
  if (/\b(iPhone|iPad|iPod)\b/.test(userAgent)) return "ios";
  if (/\bMacintosh\b/.test(userAgent) && maxTouchPoints > 1) return "ios";
  if (/\bAndroid\b/.test(userAgent)) return "android";
  return null;
}

const HINTS: Record<InstallPlatform, Omit<InstallHint, "platform">> = {
  line: {
    before: "LINE 裡不能加入主畫面。先",
    action: "用瀏覽器開啟",
    after: "，或從 LINE 選單選擇開啟，再加入主畫面。換瀏覽器後要再輸入一次密碼。"
  },
  ios: { before: "點分享按鈕 → ", action: "加入主畫面", after: "，就能像 App 一樣打開。" },
  android: { before: "點瀏覽器選單 → ", action: "加到主畫面", after: "，就能像 App 一樣打開。" }
};

export function installHint(env: InstallEnv): InstallHint | null {
  if (env.standalone) return null;
  const platform = installPlatform(env);
  return platform ? { platform, ...HINTS[platform] } : null;
}

/**
 * 換到外部瀏覽器的連結。`openExternalBrowser=1` 是 LINE 官方支援的參數。
 *
 * 帶著原本的路徑與參數，只多這一個。hash 不保留：這個 App 沒有用 hash 路由。
 */
export function externalBrowserUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.set("openExternalBrowser", "1");
  url.hash = "";
  return url.toString();
}
